import { ref, onBeforeUnmount } from 'vue'

/**
 * Long press on a floating button shows a one-line explanation of it.
 *
 * Tom 2026-09-26, on the Aa button: "One long press might explain it?" A press
 * held for `holdMs` opens the hint; releasing, or `showMs` passing, closes it.
 * One hint per press. A tap is untouched: `guardClick` lets the click through
 * unless this press opened the hint, in which case the click that follows the
 * release is swallowed so the button does not also toggle.
 */
export function useLongPressHint(holdMs = 500, showMs = 2500) {
  const hintOpen = ref(false)
  let pressTimer: ReturnType<typeof setTimeout> | null = null
  let hideTimer: ReturnType<typeof setTimeout> | null = null
  let fired = false
  let startX = 0
  let startY = 0

  const clearTimers = () => {
    if (pressTimer) clearTimeout(pressTimer)
    if (hideTimer) clearTimeout(hideTimer)
    pressTimer = null
    hideTimer = null
  }

  const onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0) return
    clearTimers()
    hintOpen.value = false
    fired = false
    startX = e.clientX
    startY = e.clientY
    pressTimer = setTimeout(() => {
      fired = true
      hintOpen.value = true
      hideTimer = setTimeout(() => { hintOpen.value = false }, showMs)
    }, holdMs)
  }

  const onPointerMove = (e: PointerEvent) => {
    // A drag is not a press: a finger sliding off cancels the pending hint.
    if (pressTimer && !fired && Math.hypot(e.clientX - startX, e.clientY - startY) > 10) {
      clearTimeout(pressTimer)
      pressTimer = null
    }
  }

  const onPointerEnd = () => {
    clearTimers()
    hintOpen.value = false
  }

  const guardClick = (fn: (e: MouseEvent) => void) => (e: MouseEvent) => {
    // Keyboard/assistive activation has no pointer press to suppress.
    const suppress = fired && e.detail !== 0
    fired = false
    if (suppress) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    fn(e)
  }

  onBeforeUnmount(clearTimers)

  return {
    hintOpen,
    pressHandlers: {
      pointerdown: onPointerDown,
      pointermove: onPointerMove,
      pointerup: onPointerEnd,
      pointercancel: () => {
        onPointerEnd()
        // Cancellation produces no click; do not swallow a later activation.
        fired = false
      },
      pointerleave: onPointerEnd,
      // The long press would otherwise raise the OS context menu / callout.
      contextmenu: (e: Event) => e.preventDefault(),
    },
    guardClick,
  }
}
